
import { Watch, Component, Vue, InjectReactive, Ref } from "vue-property-decorator";
import UploadImg from "@/components/uploadImg.vue";
import { Form } from "element-ui";
import { IHomeInstance } from "@/types/global";

import { getBoardConfigDetail, saveOrUpdateBoardConfig, BoardConfigParams } from "@/api/boardConfig";

// 系数未配置时后端默认按 1 处理，前端保持同样的兜底
const DEFAULT_COEFFICIENT = 1;
// 链接刷新间隔未配置时的默认值（秒）
const DEFAULT_REFRESH_INTERVAL = 60;
// 链接刷新间隔的合法区间（后端要求正整数且大于 15）
const REFRESH_INTERVAL_MIN = 16;
const REFRESH_INTERVAL_MAX = 100;
// 可展示终端数的合法区间
const TERMINAL_LIMIT_MIN = 1;
const TERMINAL_LIMIT_MAX = 10;
// 系数的合法区间
const COEFFICIENT_MIN = 0.01;
const COEFFICIENT_MAX = 100;

@Component({ name: 'prescription-db-smartScreenSetting',
  components: { UploadImg }
})
export default class SmartScreenSetting extends Vue {
  @Ref("formRef") formRef!: Form;
  @InjectReactive() HomeInstance !: IHomeInstance;

  merchantId = String(this.$route.query.merchantId || "");
  brandName = String(this.$route.query.brandName || "");

  form = {
    linkSwitch: false,
    refreshInterval: DEFAULT_REFRESH_INTERVAL,
    terminalLimit: undefined as number | undefined,
    aiEvalCoefficient: DEFAULT_COEFFICIENT,
    storeCountCoefficient: DEFAULT_COEFFICIENT,
    smartTerminalCoefficient: DEFAULT_COEFFICIENT,
    tcmBrainCoefficient: DEFAULT_COEFFICIENT,
    provinceCoefficient: DEFAULT_COEFFICIENT,
    cityCoefficient: DEFAULT_COEFFICIENT,
  }

  logoFileList: any[] = [];
  saveLoading = false;

  // NOTE: 这里统一用 blur 而不是 change。
  // el-input-number 的 v-model 只在失焦/回车时才写回，而内层 el-input 会在每次键入时
  // dispatch el.form.change，用 change 触发校验会读到尚未写回的旧值；
  // 若输入值与最终值相同（如输入 5 得到 5），显示值不变、watcher 不再触发，
  // 这条错误的校验结果就会一直留在界面上。改用 blur 可保证校验时读到的是真值。
  rules = {
    terminalLimit: [
      { required: true, message: "请输入可展示终端数", trigger: "blur" },
      { validator: this.validateIntegerRange(TERMINAL_LIMIT_MIN, TERMINAL_LIMIT_MAX, "可展示终端数"), trigger: "blur" },
    ],
    refreshInterval: [
      { required: true, message: "请输入链接刷新间隔", trigger: "blur" },
      { validator: this.validateIntegerRange(REFRESH_INTERVAL_MIN, REFRESH_INTERVAL_MAX, "链接刷新间隔"), trigger: "blur" },
    ],
    aiEvalCoefficient: [{ validator: this.validateCoefficient, trigger: "blur" }],
    storeCountCoefficient: [{ validator: this.validateCoefficient, trigger: "blur" }],
    smartTerminalCoefficient: [{ validator: this.validateCoefficient, trigger: "blur" }],
    tcmBrainCoefficient: [{ validator: this.validateCoefficient, trigger: "blur" }],
    provinceCoefficient: [{ validator: this.validateCoefficient, trigger: "blur" }],
    cityCoefficient: [{ validator: this.validateCoefficient, trigger: "blur" }],
  }

  @Watch("merchantId", { immediate: true, deep: true })
  initForm() {
    if (!this.merchantId) return;
    getBoardConfigDetail(this.merchantId).then((res: any) => {
      const config = res.data || {};
      if (Reflect.ownKeys(config).length <= 0) return;
      Object.assign(this.form, {
        linkSwitch: config.linkSwitch === 1 || config.linkSwitch === true,
        refreshInterval: config.refreshInterval || DEFAULT_REFRESH_INTERVAL,
        // 可展示终端数没有默认值：后端未配置时返回 0/空，统一按「未填」处理，
        // 避免受 el-input-number 最小值约束而显示成 1
        terminalLimit: config.terminalLimit || undefined,
        aiEvalCoefficient: config.aiEvalCoefficient || DEFAULT_COEFFICIENT,
        storeCountCoefficient: config.storeCountCoefficient || DEFAULT_COEFFICIENT,
        smartTerminalCoefficient: config.smartTerminalCoefficient || DEFAULT_COEFFICIENT,
        tcmBrainCoefficient: config.tcmBrainCoefficient || DEFAULT_COEFFICIENT,
        provinceCoefficient: config.provinceCoefficient || DEFAULT_COEFFICIENT,
        cityCoefficient: config.cityCoefficient || DEFAULT_COEFFICIENT,
      })
      this.logoFileList = config.logo ? [{ url: config.logo }] : [];
    })
  }

  // 必填的整数区间校验，值未填时放行（是否必填由 required 规则决定）
  validateIntegerRange(min: number, max: number, label: string) {
    return (rule: any, value: any, callback: (error?: Error) => void) => {
      if (value === undefined || value === null || value === "") return callback();
      const num = Number(value);
      if (!Number.isInteger(num) || num < min || num > max) {
        return callback(new Error(`${label}只能输入${min}-${max}之间的整数`));
      }
      callback();
    }
  }

  // 系数校验，允许小数点后两位，值未填时按默认值处理
  validateCoefficient(rule: any, value: any, callback: (error?: Error) => void) {
    if (value === undefined || value === null || value === "") return callback();
    const num = Number(value);
    if (isNaN(num) || num < COEFFICIENT_MIN || num > COEFFICIENT_MAX) {
      return callback(new Error(`系数只能输入${COEFFICIENT_MIN}-${COEFFICIENT_MAX}之间的数字`));
    }
    callback();
  }

  save() {
    this.formRef.validate().then(() => {
      const params: BoardConfigParams = {
        merchantId: this.merchantId,
        linkSwitch: this.form.linkSwitch ? 1 : 0,
        refreshInterval: this.form.refreshInterval,
        terminalLimit: this.form.terminalLimit,
        logo: this.logoFileList?.[0]?.url || "",
        aiEvalCoefficient: this.form.aiEvalCoefficient || DEFAULT_COEFFICIENT,
        storeCountCoefficient: this.form.storeCountCoefficient || DEFAULT_COEFFICIENT,
        smartTerminalCoefficient: this.form.smartTerminalCoefficient || DEFAULT_COEFFICIENT,
        tcmBrainCoefficient: this.form.tcmBrainCoefficient || DEFAULT_COEFFICIENT,
        provinceCoefficient: this.form.provinceCoefficient || DEFAULT_COEFFICIENT,
        cityCoefficient: this.form.cityCoefficient || DEFAULT_COEFFICIENT,
      }
      this.saveLoading = true;
      saveOrUpdateBoardConfig(params).then(() => {
        this.$message.success("操作成功");
        this.handleClose();
      }).finally(() => {
        this.saveLoading = false;
      })
    }).catch(() => {})
  }

  handleClose() {
    this.HomeInstance.tabRemove("smartScreenSetting");
  }
}
